// ======================================================================
// FILE: SellingQuotationOverlayDrawer.js
// MODULE: IPE → Quotation
// ======================================================================

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axiosClient from "../../../api/axiosClient";

export default function SellingQuotationOverlayDrawer({ enquiryId, onClose }) {

  const [header, setHeader] = useState(null);
  const [lines, setLines] = useState([]);
  const [mode, setMode] = useState("CATEGORY");
  const [loading, setLoading] = useState(false);

  const isEditable = header?.status_code === "draft";

  // ======================================================
  // HELPERS
  // ======================================================

  const formatCurrency = (val) => {
    return "₹ " + Number(val || 0).toLocaleString("en-IN");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "bg-success";
      case "submitted": return "bg-warning text-dark";
      case "rejected": return "bg-danger";
      default: return "bg-secondary";
    }
  };

  // ======================================================
  // LOAD
  // ======================================================

  const loadQuotation = async () => {
    try {
      const res = await axiosClient.get(
        `/api/ipe/selling-quotation/enquiry/${enquiryId}`
      );

      if (!res.data.data) return;

      setHeader(res.data.data.header);
      setLines(res.data.data.lines || []);

    } catch (err) {
      console.error("Load failed", err);
    }
  };

  useEffect(() => {
    if (enquiryId) loadQuotation();
  }, [enquiryId]);

  // ======================================================
  // DEFAULT TERMS
  // ======================================================

  useEffect(() => {
  if (!header || header.id) return; // IMPORTANT: only for new (before save)

  setHeader((prev) => ({
    ...prev,
    subject: prev.subject || "Quotation for your requirement",
    payment_terms: prev.payment_terms || "30 days from invoice date",
    delivery_schedule: prev.delivery_schedule || "4–6 weeks from order confirmation",
    terms_conditions: prev.terms_conditions || "Taxes extra as applicable. Freight extra."
  }));
}, [header]);

  // ======================================================
  // ACTIONS
  // ======================================================

  const handleGenerate = async () => {
    try {
      setLoading(true);

      await axiosClient.post("/api/ipe/selling-quotation/generate", {
        enquiry_id: enquiryId,
        mode
      });

      alert("Quotation generated ✅");
      await loadQuotation();

    } catch (err) {
      alert("Generate failed ❌");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axiosClient.post(
        `/api/ipe/selling-quotation/${header.id}/save`,
        header
      );

      alert("Saved successfully ✅");
      await loadQuotation();

    } catch (err) {
      console.error(err);
      alert("Save failed ❌");
    }
  };
  const handleDownloadPDF = async () => {
    try {
      const response = await axiosClient.get(
        `/api/ipe/selling-quotation/${header.id}/pdf`,
        {
          responseType: "blob", // VERY IMPORTANT
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `quotation_${header.id}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error("PDF Download Error:", error);
      alert("Failed to download PDF ❌");
    }
  };
  const handleSubmit = async () => {
    try {
      await axiosClient.post(
        `/api/ipe/selling-quotation/${header.id}/submit`
      );
      alert("Submitted ✅");
      await loadQuotation();
    } catch (err) {
      alert(err?.response?.data?.message || "Submit failed ❌");
    }
  };

  const handleApprove = async () => {
    try {
      await axiosClient.post(
        `/api/ipe/selling-quotation/${header.id}/approve`
      );
      alert("Approved ✅");
      await loadQuotation();
    } catch (err) {
      alert(err?.response?.data?.message || "Approve failed ❌");
    }
  };

  const handleReject = async () => {
    const remarks = prompt("Enter rejection remarks");
    if (!remarks) return;

    try {
      await axiosClient.post(
        `/api/ipe/selling-quotation/${header.id}/reject`,
        { remarks }
      );
      alert("Rejected ❌");
      await loadQuotation();
    } catch (err) {
      alert(err?.response?.data?.message || "Reject failed ❌");
    }
  };

  // ======================================================
  // UI
  // ======================================================

  const drawerUI = (

    <div style={{
      position: "fixed",
      top: 0,
      right: 0,
      width: "100vw",
      height: "100vh",
      background: "#fff",
      zIndex: 9999,
      display: "flex",
      flexDirection: "column"
    }}>

      {/* ================= HEADER ================= */}
      <div style={{
        background: "linear-gradient(90deg, #5f0cff, #7b2cff)",
        color: "#fff",
        padding: "14px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>

        <div style={{ display: "flex", gap: 12 }}>
          <b>Selling Quotation</b>

          <span className="badge bg-light text-dark">
            V{header?.version_no || 1}
          </span>

          <span className={`badge ${getStatusColor(header?.status_code)}`}>
            {header?.status_code?.toUpperCase()}
          </span>
        </div>

        <div style={{ display: "flex", gap: 8 }}>

          <button className="btn btn-light btn-sm" onClick={handleGenerate}>
            {loading ? "Generating..." : "Generate"}
          </button>

          <button
            className="btn btn-success btn-sm"
            onClick={handleSave}
            disabled={!isEditable}
          >
            Save
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSubmit}
            disabled={header?.status_code !== "draft"}
          >
            Submit
          </button>

          <button
            className="btn btn-success btn-sm"
            onClick={handleApprove}
            disabled={header?.status_code !== "submitted"}
          >
            Approve
          </button>

          <button
            className="btn btn-danger btn-sm"
            onClick={handleReject}
            disabled={header?.status_code !== "submitted"}
          >
            Reject
          </button>

          <button className="btn btn-outline-light btn-sm" onClick={onClose}>
            Close
          </button>

          <button
            className="btn btn-warning btn-sm"
            onClick={handleDownloadPDF}
            disabled={!header?.id}
          >
            Download PDF
          </button>

        </div>
      </div>

      {/* ================= BODY ================= */}
      <div style={{
        padding: "30px 60px",
        overflow: "auto",
        background: "#f9fafc"
      }}>

        {/* MODE */}
        <div style={{ marginBottom: 15 }}>
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="CATEGORY">Category</option>
            <option value="SKU">SKU</option>
          </select>
        </div>

        {/* CUSTOMER */}
        {header && (
          <div style={{
            background: "#fff",
            padding: 16,
            borderRadius: 8,
            marginBottom: 20,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
          }}>
            <h6>Customer Details</h6>

            <div style={{ fontWeight: 600, fontSize: 16 }}>
              {header.customer_name}
            </div>

            <div style={{ color: "#555" }}>
              {header.customer_address}
            </div>

            <div style={{ marginTop: 6 }}>
              Date: {header.quotation_date}
            </div>
          </div>
        )}

        {/* TABLE */}
        <table className="table table-bordered table-sm bg-white">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Description</th>
              <th>Category</th>
              <th style={{ textAlign: "right" }}>Qty</th>
              <th style={{ textAlign: "right" }}>Unit Price</th>
              <th style={{ textAlign: "right" }}>Total</th>
            </tr>
          </thead>

          <tbody>
            {lines.map((row, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{row.description}</td>
                <td>{row.category}</td>
                <td style={{ textAlign: "right" }}>{row.quantity}</td>
                <td style={{ textAlign: "right" }}>{formatCurrency(row.unit_price)}</td>
                <td style={{ textAlign: "right" }}>{formatCurrency(row.total_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* TERMS SECTION */}
        {header && (
          <div style={{
            marginTop: 30,
            background: "#fff",
            padding: 20,
            borderRadius: 10
          }}>

            <h5>Quotation Details</h5>

            <div className="mb-3">
              <label>Subject</label>
              <input
                className="form-control"
                value={header.subject || ""}
                disabled={!isEditable}
                onChange={(e) =>
                  setHeader({ ...header, subject: e.target.value })
                }
              />
            </div>

            <div className="mb-3">
              <label>Payment Terms</label>
              <textarea
                className="form-control"
                rows={2}
                value={header.payment_terms || ""}
                disabled={!isEditable}
                onChange={(e) =>
                  setHeader({ ...header, payment_terms: e.target.value })
                }
              />
            </div>

            <div className="mb-3">
              <label>Delivery Schedule</label>
              <textarea
                className="form-control"
                rows={2}
                value={header.delivery_schedule || ""}
                disabled={!isEditable}
                onChange={(e) =>
                  setHeader({ ...header, delivery_schedule: e.target.value })
                }
              />
              <div className="mb-3">
                <label>Attention Person</label>
                <input
                  className="form-control"
                  value={header.attention_person || ""}
                  disabled={!isEditable}
                  onChange={(e) =>
                    setHeader({ ...header, attention_person: e.target.value })
                  }
                />
              </div>
              <div className="mb-3">
                  <label>Exclusions</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={header.exclusions || ""}
                    disabled={!isEditable}
                    onChange={(e) =>
                      setHeader({ ...header, exclusions: e.target.value })
                    }
                  />
                </div>
                <div className="mb-3">
                  <label>Exclusions</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={header.exclusions || ""}
                    disabled={!isEditable}
                    onChange={(e) =>
                      setHeader({ ...header, exclusions: e.target.value })
                    }
                  />
                </div>
            </div>

            <div className="mb-3">
              <label>Terms & Conditions</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={header.terms_conditions || ""}
                  disabled={!isEditable}
                  onChange={(e) =>
                    setHeader({ ...header, terms_conditions: e.target.value })
                  }
                />

            </div>

          </div>
        )}

        {/* SUMMARY */}
        {header && (
          <div style={{
            marginTop: 30,
            background: "#fff",
            padding: 16,
            borderRadius: 8,
            textAlign: "right"
          }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>
              Total Amount: {formatCurrency(header.total_amount)}
            </div>
          </div>
        )}

      </div>
    </div>
  );

  return ReactDOM.createPortal(drawerUI, document.body);
}